<template>
  <v-dialog
    @keydown.esc="cancel"
    :fullscreen="$vuetify.breakpoint. smAndDown"
    persistent
    v-model="isVisible"
    v-if="isVisible"
    max-width="800px"
  >
    <div
      v-if="loading"
      layout="row"
      class="bg-white"
      style="height: 100%; min-height: 200px"
      layout-align="center center"
    >
      <v-progress-circular
        indeterminate
        color="secondary"
        size="64"
      ></v-progress-circular>
    </div>
    <v-card v-else>
      <v-card-title class="pa-0">
        <v-icon class="mr-2 ml-2">mdi-account-circle</v-icon>
        <span
          v-if="isProfile"
          class="title"
        >{{$t('users.profile')}}</span>
        <span
          v-else-if="representative.id"
          class="title"
        >{{$t('representatives.edit')}}</span>
        <span
          v-else
          class="title"
        >{{$t('representatives.new')}}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click="cancel"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text
        layout="row"
        layout-align="center center"
        class="px5-pc"
      >
        <v-form
          class="field-width mx5-pc"
          ref="representativeForm"
        >
          <div
            layout="row"
            layout-align="start center"
          >
            <div
              layout="column"
              layout-align="center center"
              flex="50"
            >
              <div
                layout="row"
                layout-align="center top"
                flex
              >
                <avatar
                  v-if="imageUrl"
                  size="75"
                  :image="imageUrl"
                ></avatar>
                <avatar
                  v-else-if="representative.avatar_id && getImageData(representative.id)"
                  size="75"
                  :image="getImageData(representative.id)"
                ></avatar>
                <avatar
                  v-else-if="representative.id"
                  :fullname="representative.firstname + ' ' + representative.lastname"
                  size="75"
                ></avatar>
                <avatar
                  v-else
                  size="75"
                ></avatar>
                <v-btn
                  v-if="imageUrl || representative.avatar_id"
                  @click="deleteAvatar"
                  :title="$t('_.delete')"
                  class="mx-0 ma0-mobile"
                  icon
                  style="top: -7px; left: -30px"
                >
                  <v-icon color="error">mdi-delete-circle</v-icon>
                </v-btn>
              </div>
            </div>
            <div
              layout="column"
              layout-align="center center"
              flex="50"
            >
              <div
                @dragover.prevent
                @drop="drag"
                @click.prevent="pickFile"
                @keyup.enter="pickFile"
                layout="column"
                layout-align="center center"
                class="upload-zone clickable text-truncate pa-1"
              >
                <span class="grey--text auto-break-text text-xs-center">{{$t('users.img_depos')}}</span>
                <span
                  v-if="imageName"
                  class="grey--text auto-break-text text-xs-center mt-1"
                >{{imageName}}</span>
              </div>
              <input
                type="file"
                style="display: none"
                ref="image"
                accept="image/*"
                @change="onFilePicked"
              >
            </div>
          </div>

          <div
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold pr-3"
            >
              {{$t('users.firstname')}}
            </div>
            <v-text-field
              v-model="representative.firstname"
              :placeholder="isProfile ? $t('representatives.your_firstname') : $t('representatives.rep_firstname')"
              :rules="[ v => !!v || $t('errors.required') ]"
              class="pl-3"
            ></v-text-field>
          </div>

          <div
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold pr-3"
            >
              {{$t('users.lastname')}}
            </div>
            <v-text-field
              v-model="representative.lastname"
              :placeholder="isProfile ? $t('representatives.your_lastname') : $t('representatives.rep_lastname')"
              :rules="[ v => !!v || $t('errors.required') ]"
              class="pl-3"
            ></v-text-field>
          </div>

          <div
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold pr-3"
            >
              Email
            </div>
            <v-text-field
              v-model="representative.email"
              :placeholder="isProfile ? $t('login.email') : $t('representatives.rep_email')"
              :rules="[ v => !!v || $t('errors.required') ]"
              class="pl-3"
            ></v-text-field>
          </div>

          <div
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold pr-3"
            >
              {{$t('representatives.phone')}}
            </div>
            <v-text-field
              v-model="representative.phone"
              :hint="$t('success.optional')"
              :placeholder="isProfile ? $t('representatives.your_phone') : $t('representatives.rep_phone')"
              persistent-hint
              class="pl-3"
            ></v-text-field>
          </div>

          <div
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold text-xs-right pr-3"
            >
              {{$t('representatives.pref_lang')}}
            </div>
            <v-select
              :placeholder="$t('nav.lang')"
              v-model="representative.lang_id"
              :items="$store.state.folangs"
              item-value="id"
              :item-text="itemName"
              :rules="[v => !!v || $t('errors.required')]"
              class="pl-3"
            ></v-select>
          </div>

          <div
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold pr-3 text-xs-right"
            >
              {{$t('representatives.spoken_langs')}}
            </div>
            <v-autocomplete
              v-model="representative.langs_ids"
              :items="langs"
              item-value="id"
              :item-text="'name.' + _locale"
              dense
              :placeholder="isProfile ? $t('representatives.your_langs') : $t('representatives.rep_langs')"
              multiple
              chips
              :rules="[ v => (!!v && !!v.length) || $t('errors.required') ]"
              deletable-chips
              class="pl-3"
            >
              <template
                slot="selection"
                slot-scope="data"
              >
                <v-chip
                  :selected="data.selected"
                  close
                  label
                  color="primary"
                  text-color="white"
                  @input="removeLang(data.item)"
                >
                  {{ i18n(data.item.name) }}
                </v-chip>
              </template>
            </v-autocomplete>
          </div>

          <div
            v-if="!isProfile"
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold pr-3"
            >
              {{$t('params.level')}}
            </div>
            <v-select
              v-model="representative.level"
              :items="levels"
              :rules="[v => !!v || $t('errors.required')]"
              class="pl-3"
              :placeholder="$t('representatives.rep_lv')"
            >
            </v-select>
          </div>

          <div
            v-if="!isProfile"
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold pr-3"
            >
              {{$t('schools.school')}}
            </div>
            <v-autocomplete
              @change="onSchoolChange"
              v-model="representative.school_id"
              :items="schools"
              item-value="id"
              item-text="name"
              dense
              :rules="[v => !!v || $t('errors.required')]"
              class="pl-3"
              :placeholder="$t('representatives.rep_school')"
            ></v-autocomplete>
          </div>

          <div
            v-if="!isProfile && sectors && sectors.length"
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold pr-3"
            >
              {{$t('params.sector')}}
            </div>
            <v-select
              v-model="representative.sector"
              :items="sectors"
              @change="onSectorChange"
              :rules="[v => !!v || $t('errors.required')]"
              class="pl-3"
              :placeholder="$t('representatives.rep_sector')"
            >
            </v-select>
          </div>

          <div
            v-if="!isProfile && representative.sector == primary"
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold pr-3"
            >
              {{$t('params.cycle')}}
            </div>
            <v-autocomplete
              v-model="representative.cycle_id"
              :items="cycles"
              item-value="id"
              item-text="label"
              dense
              :rules="[v => !!v || $t('errors.required')]"
              class="pl-3"
              :placeholder="$t('representatives.rep_cycle')"
            ></v-autocomplete>
          </div>

          <div
            v-if="!isProfile && representative.sector == secondary"
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold pr-3"
            >
              {{$t('params.regime')}}
            </div>
            <v-autocomplete
              v-model="representative.regime_id"
              :items="regimes"
              item-value="id"
              item-text="label"
              dense
              :rules="[v => !!v || $t('errors.required')]"
              class="pl-3"
              :placeholder="$t('representatives.rep_regime')"
            ></v-autocomplete>
          </div>

          <div
            v-if="!isProfile"
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold  text-xs-right pr-3"
            >
              {{$t('representatives.date_start')}}
            </div>

            <v-text-field
              v-model="startFormatted"
              class="pl-3 full-width mr-2"
              persistent-hint
              :hint="$t('representatives.start_hint')"
              :placeholder="$t('_.date_format')"
              @blur="onStartBlur"
            />
            <v-menu
              ref="menu1"
              :close-on-content-click="false"
              v-model="startDatepicker"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-x
              offset-y
              bottom
              left
              absolute
            >
              <v-icon slot="activator">event</v-icon>
              <v-date-picker
                v-model="representative.mandate_start"
                no-title
                @input="startDatepicker = false; representative.mandate_start = parseDate(representative.mandate_start)"
                :first-day-of-week="1"
                :locale="_locale"
              />
            </v-menu>
          </div>

          <div
            v-if="!isProfile"
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold  text-xs-right pr-3"
            >
              {{$t('representatives.date_end')}}
            </div>
            <v-text-field
              slot="activator"
              v-model="endFormatted"
              class="pl-3 full-width mr-2"
              :placeholder="$t('_.date_format')"
              @blur="onEndBlur"
              persistent-hint
              :hint="$t('representatives.end_hint')"
            />
            <v-menu
              ref="menu2"
              :close-on-content-click="false"
              v-model="endDatepicker"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              offset-x
              bottom
              left
              absolute
            >
              <v-icon slot="activator">event</v-icon>
              <v-date-picker
                v-model="representative.mandate_end"
                no-title
                @input="endDatepicker = false; representative.mandate_end = parseDate(representative.mandate_end)"
                :first-day-of-week="1"
                :locale="_locale"
              />
            </v-menu>
          </div>

          <div
            layout="row"
            layout-align="start center"
            flex
          >
            <div
              layout="column"
              layout-align="center end"
              flex="33"
              class="font-weight-bold  text-xs-right pr-3"
            >
              {{$t('representatives.birthday')}}
            </div>
            <v-text-field
              slot="activator"
              v-model="birthdayFormatted"
              class="pl-3 full-width mr-2"
              :hint="$t('success.optional')"
              :placeholder="$t('_.date_format')"
              persistent-hint
              @blur="onBirthdayBlur"
            />
            <v-menu
              ref="menu3"
              :close-on-content-click="false"
              v-model="birthdayDatepicker"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              offset-x
              bottom
              left
              absolute
            >
              <v-icon slot="activator">event</v-icon>
              <v-date-picker
                v-model="representative.birthday"
                no-title
                ref="birthdayPicker"
                @input="birthdayDatepicker = false; representative.birthday = parseDate(representative.birthday)"
                :first-day-of-week="1"
                :max="new Date().toISOString().substr(0, 10)"
                min="1901-01-01"
                :locale="_locale"
              />
            </v-menu>
          </div>

          <template v-if="isProfile">
              <div
                layout="row"
                layout-align="start center"
                flex
              >
                <div
                  layout="column"
                  layout-align="center end"
                  flex="33"
                  class="font-weight-bold pr-3"
                >
                  {{$t('representatives.current_pw')}}
                </div>
                <v-text-field
                  type="password" 
                  v-model="representative.current_password"
                  class="pl-3"
                  :placeholder="$t('login.pw2')"
                ></v-text-field>
              </div>
              <div
                layout="row"
                layout-align="start center"
                flex
              >
                <div
                  layout="column"
                  layout-align="center end"
                  flex="33"
                  class="font-weight-bold pr-3"
                >
                  {{$t('representatives.new_pw')}}
                </div>
                <v-text-field
                  type="password" 
                  v-model="representative.new_password"
                  class="pl-3"
                  :placeholder="$t('login.pw2')"
                ></v-text-field>
              </div>
              <div
                layout="row"
                layout-align="start center"
                flex
              >
                <div
                  layout="column"
                  layout-align="center end"
                  flex="33"
                  class="font-weight-bold pr-3"
                >
                  {{$t('representatives.conf_pw')}}
                </div>
                <v-text-field
                  type="password" 
                  v-model="representative.conf_password"
                  class="pl-3"
                  :rules="confRules"
                  :placeholder="$t('login.pw2')"
                ></v-text-field>
            </div>
          </template>
          <v-checkbox
            v-if="isProfile"
            v-model="representative.phone_displayed"
            :false-value="0"
            :true-value="1"
            :label="$t('representatives.display_num')"
            class="ml-3 my-0 py-0 mt-3"
          ></v-checkbox>

          <v-checkbox
            v-if="isProfile"
            v-model="representative.email_displayed"
            :false-value="0"
            :true-value="1"
            :label="$t('representatives.display_mail')"
            class="ml-3 my-0 py-0"
          ></v-checkbox>

          <v-checkbox
            v-model="representative.notif_disabled"
            :false-value="0"
            :true-value="1"
            :label="isProfile ? $t('representatives.notif_disabled_me') : $t('representatives.notif_disabled')"
            class="ml-3 my-0 py-0"
            :class="{'mt-3': !isProfile}"
          ></v-checkbox>

          <div class="text-xs-center mt-3">
            <v-btn
              depressed
              color="secondary"
              @click="save(false)"
              style="text-transform: none"
              :disabled="loading2"
            >{{ isProfile ? $t('_.edit_info') : $t('_.save')}}</v-btn>
            <v-btn
              v-if="!representative.id"
              depressed
              color="primary"
              @click="save(true)"
              style="text-transform: none"
              :disabled="loading2"
            >{{$t('_.save_cont')}}</v-btn>
          </div>

          <div
            v-if="isProfile"
            class="full-width"
          >
            <v-divider class="my-3"></v-divider>
            <div class="title">{{$t('representatives.close')}}</div>
            <div class="mt-2">{{$t('representatives.close_details')}}</div>
            <div
              layout="row"
              layout-align="center center"
              class="full-width mt-2"
            >
              <a
                href="#"
                @click.prevent="closeDialog = true"
                class="error--text"
              >{{$t('representatives.close_btn')}}</a>
            </div>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
    <v-dialog
      v-model="closeDialog"
      max-width="300"
    >
      <v-card>
        <v-card-title class="title">{{$t('representatives.close_dialog')}}</v-card-title>
        <v-card-text>
          {{$t('representatives.close_conf')}}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="secondary"
            flat="flat"
            @click="closeDialog = false"
          >
            {{$t('_.cancel')}}
          </v-btn>
          <v-btn
            color="error"
            depressed
            @click="closeDialog = false; closeAccess()"
          >
            {{$t('representatives.conf')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import Params from '@/api/params'
import Representatives from '@/api/representatives'
import Schools from '@/api/schools'
import I18nMixin from '@/mixins/i18n'
import Consts from '@/consts'
import ImageMixin from '@/mixins/image'
import Avatar from '@/components/shared/Avatar.vue'
import Auth from '@/plugins/auth'
import FormDataMixin from '@/mixins/formdata'
import moment from 'moment'

export default {
  mixins: [I18nMixin, ImageMixin, FormDataMixin],
  components: { Avatar },
  props: {
    value: {
      type: Object,
      required: false,
      default () { return { lang_id: 'fr', langs_ids: [], sector: null, school_id: null, birthday: null, mandate_start: null, mandate_end: null } }
    },
    visible: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  data () {
    return {
      loading: false,
      loading2: false,
      schools: [],
      langs: [],
      cycles: [],
      regimes: [],
      sectors: [],
      imgs: {},
      imgsLoad: {},
      imageData: null,
      imageName: null,
      imageUrl: null,
      birthdayDatepicker: false,
      startDatepicker: false,
      endDatepicker: false,
      closeDialog: false,
      schoolId: null
    }
  },
  computed: {
    representative: {
      get () {
        return this.value
      },
      set (x) {
        this.$emit('input', x)
      }
    },
    isVisible: {
      get () {
        return this.visible
      },
      set (x) {
        this.$emit('update:visible', x)
      }
    },
    levels () {
      return [
        { value: Consts.NATIONAL, text: this.$t('users.lv_nat') },
        { value: Consts.SECTORAL, text: this.$t('users.lv_sec') },
        { value: Consts.LOCAL, text: this.$t('users.lv_loc') },
      ]
    },
    primary () {
      return Consts.PRIMARY
    },
    secondary () {
      return Consts.SECONDARY
    },
    isProfile () {
      if (Auth.user.id == this.representative.id) {
        return true
      }
      return false
    },
    birthdayFormatted: {
      get () {
        return this.representative.birthday ? this.formatDateI18n(this.representative.birthday) : null
      },
      set (x) {
        this.$emit('input', x)
      }
    },
    startFormatted: {
      get () {
        return this.representative.mandate_start ? this.formatDateI18n(this.representative.mandate_start) : null
      },
      set (x) {
        this.$emit('input', x)
      }
    },
    endFormatted: {
      get () {
        return this.representative.mandate_end ? this.formatDateI18n(this.representative.mandate_end) : null
      },
      set (x) {
        this.$emit('input', x)
      }
    },
    confRules () {
        if(!this.representative.new_password){
          return true
        }
        
        return [
          v => !!v || this.$t('errors.required'),
          v => (v && (v === this.representative.new_password)) || this.$t('representatives.conf_err')
        ]
    }
  },
  mounted () {
    if (this.representative.school_id) {
      this.schoolId = this.representative.school_id
    }
  },
  watch: {
    isVisible (val) {
      if (val) {
        this.fetchParams()
        this.imgs = {}
        this.imgsLoad = {}

        if (this.representative.avatar_id) {
          this.getImage('/assets/users/' + this.representative.id, this.representative.id)
        }
      }
    },
    birthdayDatepicker (val) {
      val && setTimeout(() => (this.$refs.birthdayPicker.activePicker = 'YEAR'))
    }
  },
  methods: {
    fetchParams () {
      Schools.getList({ sortBy: 'name', is_enabled: 1, current_id: this.representative.school_id }).then(res => {
        this.schools = res.data

        if (this.representative.school_id) {
          this.onSchoolChange(this.representative.school_id, false)
        }
      })
      Params.getList('cycles', { is_enabled: 1, current_id: this.representative.cycle_id, sortBy: 'label' }).then(res => {
        this.cycles = res.data
      })
      Params.getList('regimes', { is_enabled: 1, current_id: this.representative.regime_id, sortBy: 'label' }).then(res => {
        this.regimes = res.data
      })
      Params.getList('langs', { is_enabled: 1, current_id: this.representative.langs_ids.length ? this.representative.langs_ids : null }).then(res => {
        this.langs = res.data
      })
    },
    cancel () {
      this.$nextTick(() => {
        this.clearForm()
      })
      this.isVisible = false
    },
    save (cont = false) {
      if (!this.$refs['representativeForm'].validate()) {
        return
      }

      this.loading2 = true

      // Convert the data to FormData for proper file upload
      let formData = new FormData()
      this._convertObjectToFormData(formData, this.representative)

      if (this.imageData) {
        formData.append("avatar", this.imageData, this.imageName)
      }

      if (!this.representative.id) {
        Representatives.create(formData).then(res => {
          if (!cont) {
            this.clearForm()
            this.isVisible = false
          }

          this.loading2 = false

          this.$emit('created', res.data)
          this.$snotify.success(this.$t('representatives.created'))

          if (cont) {
            this.clearForm()

            this.$nextTick(() => {
              this.representative.lang_id = 'fr'
              this.representative.firstname = ""
              this.representative.lastname = ""
              this.representative.langs_ids = []
              this.representative.sector = null
              this.representative.birthday = null
              this.representative.mandate_start = null
              this.representative.mandate_end = null
              this.representative.phone = null
              this.representative.email = ""
              this.representative.level = null
              this.representative.cycle_id = null
              this.representative.regime_id = null
              this.representative.avatar_id = null
              this.representative.school_id = this.schoolId ? this.schoolId : null

              if (this.representative.school_id) {
                this.onSchoolChange(this.representative.school_id, true)
              }
            })
          }
        }, () => {
          this.loading2 = false
        })
      } else {
        Representatives.updateAsForm(this.representative.id, formData).then(res => {
          this.clearForm()
          this.isVisible = false
          this.loading2 = false
          this.$emit('updated', res.data)
          this.$snotify.success(this.isProfile ? this.$t('representatives.profile_updated') : this.$t('representatives.edited'))
          if (this.isProfile) {
            Auth.fetchUser()
          }
        }, () => {
          this.loading2 = false
        })
      }
    },
    itemName (item) {
      return this.i18n(item.name)
    },
    itemLabel (item) {
      return this.i18n(item.label)
    },
    onStartBlur (val) {
      let date = this.parseDate(val.target.value)
      if (this.representative.mandate_start != date) {
        this.representative.mandate_start = date
      }
    },
    onEndBlur (val) {
      let date = this.parseDate(val.target.value)
      if (this.representative.mandate_end != date) {
        this.representative.mandate_end = date
      }
    },
    onBirthdayBlur (val) {
      let date = this.parseDate(val.target.value)
      if (this.representative.birthday != date) {
        this.representative.birthday = date
      }
    },
    clearForm () {
      this.$refs['representativeForm'].reset()
      this.imageData = null
      this.imageName = null
      this.imageUrl = null
    },
    deleteAvatar () {
      if (this.representative.id && this.representative.avatar_id) {
        Representatives.deleteAvatar(this.representative.id)
        if (this.isProfile) {
          Auth.fetchUser()
        }
      }
      this.imageData = null
      this.imageName = null
      this.imageUrl = null
      this.representative.avatar_id = null
      this.$emit('avatarDeleted')
    },
    pickFile () {
      this.$refs.image.click()
    },
    drag (e) {
      e.stopPropagation()
      e.preventDefault()
      var files = e.dataTransfer.files
      if (files[0] !== undefined) {
        this.imageData = files[0]
        this.imageName = files[0].name
        this.imageUrl = URL.createObjectURL(this.imageData)
      }
    },
    onFilePicked (e) {
      const files = e.target.files
      if (files[0] !== undefined) {
        this.imageData = files[0]
        this.imageName = files[0].name
        this.imageUrl = URL.createObjectURL(this.imageData)
      }
    },
    removeLang (item) {
      const index = this.representative.langs_ids.indexOf(item.id)
      if (index >= 0) this.representative.langs_ids.splice(index, 1)
    },
    onSchoolChange (s, reset = true) {
      let key = this.schools.map(function (i) { return i.id }).indexOf(s)
      let school = this.schools[key]
      this.sectors = []

      if (reset) {
        this.representative.sector = null
        this.representative.cycle_id = null
        this.representative.regime_id = null
      }

      if (school.sectors && school.sectors.length && school.sectors.length > 1) {
        for (let i = 0; i < school.sectors.length; i++) {
          switch (school.sectors[i].sector) {
            case Consts.PRIMARY:
              this.sectors.push({ value: Consts.PRIMARY, text: this.$t('schools.sector_prim') })
              break
            case Consts.SECONDARY:
              this.sectors.push({ value: Consts.SECONDARY, text: this.$t('schools.sector_sec') })
              break
            case Consts.COMPETENCECENTRE:
              this.sectors.push({ value: Consts.COMPETENCECENTRE, text: this.$t('schools.sector_comp') })
              break
          }
        }
      } else {
        this.representative.sector = school.sectors[0].sector
      }
    },
    onSectorChange (s) {
      switch (s) {
        case Consts.PRIMARY:
          this.representative.regime_id = null
          break
        case Consts.SECONDARY:
          this.representative.cycle_id = null
          break
        case Consts.COMPETENCECENTRE:
          this.representative.regime_id = null
          this.representative.cycle_id = null
          break
      }
    },
    parseDate (date) {
      if (!date) return null

      if (date.indexOf('-') >= 0) {
        return date
      }

      return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
    },
    closeAccess () {
      this.loading = true
      Representatives.delete(this.representative.id).then(() => {
        this.loading = false
        this.closeDialog = false
        this.isVisible = false
        this.$snotify.success(this.$t('representatives.deleted'))
        Auth.localLogout()
        this.$router.push('/login')
      }, () => {
        this.loading = false
      })
    }
  }
}
</script>
<style scoped>
.upload-zone {
  width: 100%;
  height: 150px;
  border: 2px dashed grey;
  border-radius: 9% 9%;
}
</style>